import React, { useEffect, useState } from "react";
import { Modal, Input, Typography, Alert, Button, Space, Tag } from "antd";

const { TextArea } = Input;

const SerialNumberModal = ({
  open,
  row,
  onSave,
  onCancel,
  isTatmeenEnabled = false,
}) => {
  const [serialText, setSerialText] = useState("");
  const [errors, setErrors] = useState([]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [requiredCount, setRequiredCount] = useState(0);
  const [isRequired, setIsRequired] = useState(false);

  useEffect(() => {
    if (!row || !open) return;

    const mode = row.serial_mode ?? "unit";
    const tatmeenRequired = row.is_tatmeen_required || false;

    setIsBatchMode(mode === "batch");
    setIsRequired(tatmeenRequired);

    const required = tatmeenRequired
      ? mode === "batch"
        ? 1
        : Number(row.quantity || 0)
      : 0;

    setRequiredCount(required);

    const existingSerials = row.serials || [];
    setSerialText(existingSerials.join("\n"));
    setErrors([]);
  }, [open, row]);

  const validateSerials = () => {
    // If serials are not required, validation passes
    if (!isRequired) {
      return {
        valid: true,
        serials: [],
      };
    }

    const serials = serialText
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);

    const validationErrors = [];
    const seen = new Set();

    // Count validation
    if (isBatchMode) {
      if (serials.length !== 1) {
        validationErrors.push(
          "Batch mode requires exactly 1 serial number."
        );
      }
    } else {
      if (serials.length !== requiredCount) {
        validationErrors.push(
          `Expected ${requiredCount} serial numbers but got ${serials.length}.`
        );
      }
    }

    // Duplicate validation
    serials.forEach((serial, index) => {
      if (seen.has(serial)) {
        validationErrors.push(
          `Line ${index + 1}: Duplicate serial number "${serial}"`
        );
      }
      seen.add(serial);
    });

    setErrors(validationErrors);

    return {
      valid: validationErrors.length === 0,
      serials,
    };
  };

  const handleSave = () => {
    const result = validateSerials();

    if (result.valid) {
      onSave(result.serials);
    }
  };

  const addEmptyLines = () => {
    const current = serialText
      .split("\n")
      .filter((x) => x.trim() !== "");

    if (current.length < requiredCount) {
      const newSerials = [...current];

      while (newSerials.length < requiredCount) {
        newSerials.push("");
      }

      setSerialText(newSerials.join("\n"));
    }
  };

  const enteredCount = serialText
    .split("\n")
    .filter((x) => x.trim())
    .length;

  return (
    <Modal
      open={open}
      title={
        <Space>
          <span>Manage Serials</span>

          {!isRequired && (
            <Tag color="default">Not Required</Tag>
          )}

          {isRequired && isBatchMode && (
            <Tag color="blue">Batch Mode</Tag>
          )}

          {isRequired && !isBatchMode && (
            <Tag color="green">Quantity Mode</Tag>
          )}

          {isRequired && (
            <Tag color="orange">
              {requiredCount} Required
            </Tag>
          )}

          {isTatmeenEnabled && isRequired && (
            <Tag color="purple">GS1</Tag>
          )}
        </Space>
      }
      onOk={handleSave}
      onCancel={onCancel}
      width={900}
      okText={isRequired ? "Save Serials" : "Close"}
      okButtonProps={{
        disabled: !isRequired, // Disable save if serials not required
      }}
    >
      {!isRequired ? (
        <Alert
          message="Serials Not Required"
          description="This item does not require serial numbers. Tatmeen is not enabled for this item."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <>
          <Alert
            message={
              isBatchMode
                ? "Batch Serial Mode"
                : "Quantity Serial Mode"
            }
            description={
              isBatchMode
                ? "Enter exactly 1 serial number for this batch."
                : `Enter ${requiredCount} serial number${requiredCount > 1 ? "s" : ""
                }, one per line.`
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {isTatmeenEnabled && (
            <Alert
              message="GS1 Serial Number Format"
              description="Serials should follow GS1 format for UAE compliance."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {!isBatchMode && enteredCount < requiredCount && (
            <Button
              size="small"
              onClick={addEmptyLines}
              style={{ marginBottom: 10 }}
            >
              Add Empty Lines ({requiredCount - enteredCount} remaining)
            </Button>
          )}

          <TextArea
            autoSize={{ minRows: 12, maxRows: 30 }}
            style={{
              fontFamily: "monospace",
              fontSize: 13,
            }}
            value={serialText}
            onChange={(e) => {
              setSerialText(e.target.value);

              if (errors.length) {
                setErrors([]);
              }
            }}
          />

          {errors.length > 0 && (
            <Alert
              message={`${errors.length} validation error(s)`}
              description={errors.map((err, i) => (
                <div key={i}>• {err}</div>
              ))}
              type="error"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}

          <Space style={{ marginTop: 12 }} size="large">
            <Typography.Text>
              <strong>Required:</strong> {requiredCount}
            </Typography.Text>

            <Typography.Text>
              <strong>Entered:</strong> {enteredCount}
            </Typography.Text>

            <Typography.Text>
              <strong>Mode:</strong>{" "}
              {isBatchMode
                ? "Batch (1 serial)"
                : `Quantity (${requiredCount} serials)`}
            </Typography.Text>
          </Space>
        </>
      )}
    </Modal>
  );
};

export default SerialNumberModal;